// A full-screen colour wash whenever `trigger` changes to a new truthy value: the colour floods the screen in the first
// 150 ms, holds for a beat, then fades to leave the screen, so the answer underneath is readable again well inside the
// reveal. Opacity only, on a layer that never takes a tap. Sits under the confetti (z 70) and the flash (z 60).
//   portal (default): a fixed layer on the body, covering the viewport (the phone).
//   portal={false}: an absolutely positioned layer filling its parent (the stage canvas, which is scaled to fit the window).
// Reduced motion: a quieter, slower fade with no hold (the wash becomes a fade).
import { useRef } from 'react';
import { createPortal } from 'react-dom';
import { useFire } from './fire';
import { canAnimate, prefersReducedMotion } from './reduced';

export const WASH_DEFAULT_MS = 1400;

export function Wash({ trigger, color, durationMs = WASH_DEFAULT_MS, peak = 0.5, portal = true, zIndex = 58 }: {
  trigger: unknown; color: string; durationMs?: number;
  /** Opacity at the top of the wash, 0 to 1. */
  peak?: number;
  portal?: boolean;
  zIndex?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useFire(trigger, () => {
    const el = ref.current; if (!el || !canAnimate()) return;
    const soft = prefersReducedMotion();
    const frames: Keyframe[] = soft
      ? [{ opacity: 0 }, { opacity: Math.min(peak, 0.22), offset: 0.4 }, { opacity: 0 }]
      : [{ opacity: 0 }, { opacity: peak, offset: 0.11 }, { opacity: peak * 0.92, offset: 0.42 }, { opacity: 0 }];
    const a = el.animate(frames, { duration: soft ? durationMs * 1.3 : durationMs, easing: soft ? 'ease-in-out' : 'ease-out' });
    return () => a.cancel();
  });
  const layer = (
    <div ref={ref} aria-hidden="true" data-rd-wash="" data-color={color}
      style={{ position: portal ? 'fixed' : 'absolute', inset: 0, background: color, opacity: 0, pointerEvents: 'none', zIndex, willChange: 'opacity' }} />
  );
  if (!portal) return layer;
  if (typeof document === 'undefined') return null;
  return createPortal(layer, document.body);
}
